import React from 'react';
import { Camera, AlertCircle, CheckCircle } from 'lucide-react';

interface QRScanStatusOverlayProps {
  status: 'idle' | 'scanning' | 'success' | 'timeout';
  onCancel?: () => void;
  onRetry?: () => void;
  onTestSuccess?: () => void;
}

export function QRScanStatusOverlay({ status, onCancel, onRetry, onTestSuccess }: QRScanStatusOverlayProps) {
  if (status === 'idle' || status === 'success') return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60">
      <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-2xl">
        {status === 'scanning' && (
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <div className="relative">
                <Camera className="w-16 h-16 text-amber-600" />
                <div className="absolute inset-0 animate-ping">
                  <Camera className="w-16 h-16 text-amber-400 opacity-75" />
                </div>
              </div>
            </div>
            <h3 className="text-xl font-semibold text-gray-900">
              Opening Camera...
            </h3>
            <p className="text-gray-600">
              Please scan a QR code when the camera opens
            </p>
            <div className="flex justify-center">
              <div className="w-8 h-8 border-4 border-amber-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
            {onCancel && (
              <button
                onClick={onCancel}
                className="mt-4 px-6 py-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                Cancel
              </button>
            )}
          </div>
        )}

        {status === 'timeout' && (
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <AlertCircle className="w-16 h-16 text-orange-500" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900">
              No Scan Detected
            </h3>
            <p className="text-gray-600">
              The camera opened but no QR code was scanned. This could mean:
            </p>
            <ul className="text-left text-sm text-gray-600 space-y-2">
              <li>• You closed the scanner without scanning</li>
              <li>• The QR code wasn't recognized</li>
              <li>• There was a connection issue</li>
            </ul>
            <div className="flex gap-3 mt-6">
              {onRetry && (
                <button
                  onClick={onRetry}
                  className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-xl hover:bg-amber-700 transition-colors"
                >
                  Try Again
                </button>
              )}
              {onCancel && (
                <button
                  onClick={onCancel}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
              )}
            </div>
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-xs text-blue-800">
                <strong>Debug Info:</strong> Check the browser console for detailed logs (search for "[QR Scan]")
              </p>
            </div>
            {onTestSuccess && (
              <div className="mt-3">
                <button
                  onClick={onTestSuccess}
                  className="w-full px-4 py-2 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors text-sm"
                >
                  Test Success Modal
                </button>
                <p className="text-xs text-gray-500 mt-2 text-center">
                  This will show what happens when a scan succeeds
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
